<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Orders">
      <router-link :to="{ name: 'OrderCreate' }" class="btn btn-primary">
        Add Order
      </router-link>

      <!-- <div slot="subheader" class="mt-4 sm:mt-0">
        <NavTabs 
          :tabs="[
            { name: 'Bookings', route: { name: 'BookingList' } },
            { name: 'Orders', route: { name: 'OrderList' } },
          ]" 
        />
      </div> -->
    </PageHeader>

    <div class="container">
      <!-- Loading -->
      <Loading v-if="loading" class="p-6" />
      <!-- List -->
      <div class="card" v-else>
        <!-- Header -->
        <div class="flex flex-col justify-between p-4 border-b xl:flex-row lg:p-5">
          <div class="grid grid-cols-2 gap-3 lg:flex">
            <!-- Search -->
            <fieldset class="relative col-span-2">
              <fa :icon="['far', 'search']" class="absolute top-0 left-0 m-2 text-gray-400 pointer-events-none" />
              <input type="text" class="py-1 pl-8 form-input" placeholder="Search Order ID" v-model="filter.search" @keydown.enter="getOrders">
            </fieldset>
            <!-- Filter -->
            <FilterDropdown @apply="applyFilter" @clear="clearFilter" :loading="loading">
              <div>
                <label for="date" class="form-label">Date Range</label>
                <DatePicker  color="indigo" mode="dateTime" is-range is24hr :model-config="{ start: { timeAdjust: '00:00:00' }, end: { timeAdjust: '23:59:59' } }" v-model="filter.orderdate">
                  <template v-slot="{ inputValue, inputEvents }">
                    <div class="flex justify-center">
                      <input
                        :value="inputValue.start"
                        v-on="inputEvents.start"
                        class="form-input prepend"
                      />
                      <div class="flex items-center justify-center flex-none w-10 text-gray-500 border-t border-b border-gray-300">
                        &rarr;
                      </div>
                      <input
                        :value="inputValue.end"
                        v-on="inputEvents.end"
                        class="form-input append"
                      />
                    </div>
                  </template>
                </DatePicker>
              </div>
            </FilterDropdown>
            <!-- Sort -->
            <SortDropdown :options="sortOptions" />
          </div>

          <div class="mt-2 xl:mt-0">
            <ButtonToggle
              class="w-full"
              :enabled="account.order_sync_enabled"
              :status-text="account.order_sync_enabled ? 'Order Sync is Enabled' : 'Order Sync is Disabled'"
              :button-text="account.order_sync_enabled ? 'Disable Order Sync' : 'Enable Order Sync'" 
              :updating="submitting"
              @toggle="toggleOrderSync" />
          </div>
        </div>
        
        <!-- No Results -->
        <div v-if="orders.meta.total < 1" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            You don't have any orders yet.
          </div>
          <div class="text-gray-500">
            Orders are generated from bookings, or sync'd from WEBFLEET automatically.
          </div>
        </div>

        <!-- Results -->
        <div v-else>
          <!-- List -->
          <div class="divide-y">
            <router-link v-for="order in orders.data" :key="order.id" :to="{ name: 'OrderEdit', params: { id: order.id } }" class="relative block text-gray-800 hover:bg-gray-50 hover:no-underline">
              <div class="relative grid items-center grid-cols-6 gap-4 px-4 py-4 lg:grid-cols-12 sm:px-6">
                <div class="col-span-3">
                  <div class="flex items-center">
                    <div>
                      <div class="font-medium">
                        {{ order.orderdate | moment('ddd D MMMM') }}
                      </div>
                      <div>
                        <div class="max-w-full text-gray-500 truncate">
                        {{ order.orderid }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-span-3">
                  <div class="font-medium">
                    {{ parseOrderType(order.ordertype) }}
                  </div>
                  <div class="flex">
                    <badge v-if="order.geocoding_error || order.uploading_error" class="py-1 text-red-600 truncate bg-red-100">
                      <div slot="prefix" class="pl-2">
                        <div class="p-1 bg-current rounded-full"></div>
                      </div>
                      Error
                    </badge>
                    <badge v-else-if="!order.latitude || !order.longitude" class="py-1 truncate text-cyan-600 bg-cyan-100">
                      <div slot="prefix" class="pl-2">
                        <div class="p-1 bg-current rounded-full"></div>
                      </div>
                      Geocoding...
                    </badge>
                    <badge v-else-if="!order.in_webfleet" class="py-1 truncate text-cyan-600 bg-cyan-100">
                      <div slot="prefix" class="pl-2">
                        <div class="p-1 bg-current rounded-full"></div>
                      </div>
                      Uploading...
                    </badge>
                    <badge v-else class="py-1 text-gray-500 truncate bg-gray-100" :class="parseOrderStateClass(order.orderstate)">
                      <div slot="prefix" class="pl-2">
                        <div class="p-1 bg-current rounded-full"></div>
                      </div>
                      {{ parseOrderStateLabel(order.orderstate) }}
                    </badge>
                  </div>
                </div>

                <div class="col-span-5 truncate whitespace-nowrap">
                  <div class="w-full font-medium truncate">
                    {{ order.contact_name }}
                  </div>
                  <div class="w-full text-gray-500 truncate">
                    {{ order.formatted_address }}
                  </div>
                </div>

                <div class="flex justify-end col-span-1">
                  <router-link v-if="order.tracking_code" :to="{ name: 'TrackingLinkStatus', params: { trackingCode: order.tracking_code } }" class="btn btn-sm btn-secondary">
                    Track
                  </router-link>
                </div>
              </div>
            </router-link>
          </div>
          <!-- Pagination -->
          <Pagination class="border-t rounded-b-lg" :total="orders.meta.total" :per-page="orders.meta.per_page" :current-page="orders.meta.current_page" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { sub, add } from 'date-fns'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import FilterDropdown from '@/components/FilterDropdown'
import SortDropdown from '@/components/SortDropdown'
import ButtonToggle from '@/components/ButtonToggle'

export default {
  name: 'ListOrders',

  metaInfo: {
    title: 'Orders'
  },

  components: {
    DatePicker,
    FilterDropdown,
    SortDropdown,
    ButtonToggle
  },

  created () {
    this.setFilter()
    this.getOrders()
  },

  computed: {
    ...mapGetters(['account'])
  },

  data () {
    return {
      loading: true,
      submitting: false,
      orders: {},
      filter: {
        search: '',
        orderdate: { start: sub(new Date(), { months: 1 }), end: add(new Date(), { months: 1 }) }
      },
      sortOptions: [
        { label: `Date <span class="text-gray-500">(Oldest First)</span>`, prop: 'orderdate', dir: 'asc' },
        { label: `Date <span class="text-gray-500">(Newest First)</span>`, prop: 'orderdate', dir: 'desc' },
        { label: `Type <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'ordertype', dir: 'desc' },
        { label: `Type <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'ordertype', dir: 'asc' },
        { label: `Address <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'formatted_address', dir: 'asc' },
        { label: `Address <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'formatted_address', dir: 'desc' },
        { label: `ID <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'orderid', dir: 'asc' },
        { label: `ID <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'orderid', dir: 'desc' },
      ]
    }
  },

  methods: {
    parseOrderType (code) {
      const typeEnum = {
        '1': 'Service',
        '2': 'Pickup',
        '3': 'Delivery'
      }
      return typeEnum[code.toString()]
    },

    parseOrderStateLabel (code) {
      if (code === 0) { return 'Not Sent' }
      else if (code >= 100 && code < 200) { return 'Sent' }
      else if (code >= 201 && code < 300) { return 'In Progress' }
      else if (code === 301) { return 'Cancelled' }
      else if (code === 302) { return 'Rejected' }
      else if (code === 401) { return 'Complete' }
    },

    parseOrderStateClass (code) {
      if (code === 0) { return 'text-gray-400 bg-gray-100 bg-opacity-50' }
      else if (code >= 100 && code < 200) { return 'text-yellow-500 bg-yellow-100 bg-opacity-50' }
      else if (code >= 201 && code < 300) { return 'text-blue-500 bg-blue-100 bg-opacity-50' }
      else if (code === 301) { return 'text-orange-500 bg-orange-100 bg-opacity-50' }
      else if (code === 302) { return 'text-orange-500 bg-orange-100 bg-opacity-50' }
      else if (code === 401) { return 'text-emerald-500 bg-emerald-100 bg-opacity-50' }
    },
    
    async getOrders () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/orders`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'orderdate',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.orders = data.orders
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        search: '',
        orderdate: { start: sub(new Date(), { months: 1 }), end: add(new Date(), { months: 1 }) }
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    },

    async toggleOrderSync () {
      const switchTo = !this.account.order_sync_enabled

      try {
        this.submitting = true
        await this.$api.patch(`account/${this.$route.params.account}/accounts/${this.account.id}`, {
          ...this.account,
          order_sync_enabled: switchTo
        })
        await this.$store.dispatch('syncUser', this)
        this.$toasted.show(`Order sync ${switchTo ? 'enabled' : 'disabled'}.`)
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      } finally {
        this.submitting = false
      }
    }
  },

  watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getOrders()
      },
      deep: true
    }
  }
}
</script>